import createAdFoxContainerManager, { PLACEMENT_CLASSES, LOADER_CLASS } from './AdFoxContainerManager';

const ALWAYS_SHOW_STUB_TYPES = new Set(['a1', 'a51']);
const PLACEMENT_TYPES = { BR1: 'br1', BR2: 'br2' };
const ERROR_CODES_TO_HIDE = new Set([404, 402]);

function getPlacementType(placementId) {
    if (!placementId) return '';
    const parts = placementId.split('.');
    if (parts.length < 3) return '';
    return parts[2].toLowerCase();
}

function extractErrorCode(error) {
    if (!error) return null;

    if (error.status !== undefined) return Number(error.status);
    if (error.code !== undefined) return Number(error.code);

    if (error.type !== undefined) {
        const parsed = Number.parseInt(error.type, 10);
        if (!Number.isNaN(parsed)) return parsed;
    }

    const { message } = error;
    if (typeof message === 'string') {
        if (message.includes('404')) return 404;
        if (message.includes('402')) return 402;
    }

    return null;
}


function createAdFoxHelper() {
    const adFoxRegisteredContainers = new Set();
    const containerManager = createAdFoxContainerManager();

    function isSidebarPlacement(placementId) {
        const type = getPlacementType(placementId);
        return type === PLACEMENT_TYPES.BR1 || type === PLACEMENT_TYPES.BR2;
    }

    function checkAdFoxAvailability() {
        try {
            return !!(window.Ya && window.Ya.adfoxCode);
        } catch (error) {
            console.error('[AdFoxHelper] AdFox API not available:', error);
            return false;
        }
    }

    function destroyAdFox(containerId) {
        try {
            if (adFoxRegisteredContainers.has(containerId) && window.Ya?.adfoxCode?.destroy) {
                window.Ya.adfoxCode.destroy(containerId);
            }
        } catch (error) {
            console.warn('[AdFoxHelper] Error destroying AdFox container:', error);
        }
    }

    function adFoxCreate(adfoxParams, containerId) {
        window.Ya.adfoxCode.create(adfoxParams);
        adFoxRegisteredContainers.add(containerId);
    }

    function buildCallbacks(containerId, placementId, isAlwaysStub, onSidebarError) {
        return {
            // eslint-disable-next-line func-names
            onRender (data) {
                const attrs = data?.attributes;
                const container = containerManager.getContainer(containerId);

                const imageAttrs = (data?.imageUrl ? data : null)
                    || (attrs?.imageUrl ? attrs : null);

                if (imageAttrs?.imageUrl && container) {
                    const link = document.createElement('a');
                    link.href = imageAttrs.reference || '#';
                    link.target = imageAttrs.target || '_blank';
                    link.rel = 'noopener noreferrer';

                    const img = document.createElement('img');
                    img.src = imageAttrs.imageUrl;
                    img.alt = imageAttrs.alt || '';
                    if (imageAttrs.width) img.width = Number(imageAttrs.width);
                    if (imageAttrs.height) img.height = Number(imageAttrs.height);

                    link.append(img);
                    container.innerHTML = '';
                    container.append(link);
                }

                containerManager.applyState(containerId, 'loaded', isAlwaysStub);
            },

            onStub: () => {
                containerManager.applyState(containerId, 'stub', isAlwaysStub);

                if (isSidebarPlacement(placementId) && typeof onSidebarError === 'function') {
                    onSidebarError(placementId);
                }
            },

            onError: (error) => {
                containerManager.applyState(containerId, 'error', isAlwaysStub);

                const errorCode = extractErrorCode(error);
                if (
                    ERROR_CODES_TO_HIDE.has(errorCode)
                    && isSidebarPlacement(placementId)
                    && typeof onSidebarError === 'function'
                ) {
                    onSidebarError(placementId, errorCode);
                }
            }
        };
    }

    function create({ containerId, customParam1, customParam2, ownerId, placementId, onSidebarError, countryId }) {
        try {
            const container = containerManager.getContainer(containerId);
            if (!container) {
                console.error(`[AdFoxHelper] Container ${containerId} not found`);
                return false;
            }

            const isAlwaysStub = ALWAYS_SHOW_STUB_TYPES.has(getPlacementType(placementId));
            const prevState = containerManager.containerStates.get(containerId);
            const hasLoaderOnly = container.children.length === 1
                && !!container.querySelector(`.${LOADER_CLASS}`);
            const isEmpty = !container.innerHTML.trim() || hasLoaderOnly;

            if (adFoxRegisteredContainers.has(containerId)
                && (prevState === 'stub' || prevState === 'error' || isEmpty)) {
                destroyAdFox(containerId);
                adFoxRegisteredContainers.delete(containerId);
                containerManager.prepareForRecreate(containerId);
            }

            const isReload = adFoxRegisteredContainers.has(containerId);

            container.classList.remove(
                PLACEMENT_CLASSES.STUB,
                PLACEMENT_CLASSES.ERROR,
                PLACEMENT_CLASSES.LOADED
            );

            if (!isReload || isEmpty) {
                containerManager.applyState(containerId, 'loading', isAlwaysStub);
            }

            const params = { p1: customParam1, p2: customParam2, puid: Date.now() };
            if (countryId !== undefined && countryId !== null) {
                params.pcountry = countryId;
            }

            setTimeout(() => {
                try {
                    const containerWidth = containerManager.getContainerWidth(container);

                    if (!isAlwaysStub && (!isReload || isEmpty)) {
                        containerManager.hideContainer(container);
                    }
                    if (!isReload || isEmpty) {
                        container.style.width = `${containerWidth}px`;
                    }

                    const adfoxParams = {
                        ownerId,
                        containerId,
                        platform: 'desktop',
                        width: containerWidth,
                        params,
                        ...buildCallbacks(containerId, placementId, isAlwaysStub, onSidebarError)
                    };

                    if (checkAdFoxAvailability()) {
                        if (isReload && window.Ya.adfoxCode.reload) {
                            window.Ya.adfoxCode.reload(containerId, params);
                            if (!isEmpty) {
                                containerManager.applyState(containerId, 'loaded', isAlwaysStub);
                            }
                        } else {
                            adFoxCreate(adfoxParams, containerId);
                        }
                        return;
                    }

                    window.yaContextCb = window.yaContextCb || [];
                    window.yaContextCb.push(() => {
                        if (!checkAdFoxAvailability()) {
                            console.warn('[AdFoxHelper] AdFox not available');
                            containerManager.applyState(containerId, 'stub', isAlwaysStub);
                            return;
                        }
                        try {
                            adFoxCreate(adfoxParams, containerId);
                        } catch (error) {
                            console.error('[AdFoxHelper] Error creating banner:', error);
                            containerManager.applyState(containerId, 'error', isAlwaysStub);
                        }
                    });
                } catch (error) {
                    console.error('[AdFoxHelper] Error in deferred banner creation:', error);
                    containerManager.applyState(containerId, 'error', isAlwaysStub);
                }
            }, 0);

            return true;
        } catch (error) {
            console.error('[AdFoxHelper] Error in create:', error);
            return false;
        }
    }

    function clear(containerId, keepRegistered = false) {
        try {
            if (!keepRegistered) {
                destroyAdFox(containerId);
            }
            containerManager.clearContainer(containerId, keepRegistered);
            if (!keepRegistered) {
                adFoxRegisteredContainers.delete(containerId);
            }
        } catch (error) {
            console.error('[AdFoxHelper] Error clearing:', error);
        }
    }

    function show(containerId) {
        try {
            const container = containerManager.getContainer(containerId);
            if (container) {
                container.style.display = 'block';
                container.classList.add(PLACEMENT_CLASSES.VISIBLE);
            }
        } catch (error) {
            console.error('[AdFoxHelper] Error showing:', error);
        }
    }

    function hide(containerId) {
        try {
            const container = containerManager.getContainer(containerId);
            if (container) {
                container.style.display = 'none';
                container.classList.remove(PLACEMENT_CLASSES.VISIBLE);
            }
        } catch (error) {
            console.error('[AdFoxHelper] Error hiding:', error);
        }
    }

    function showLoader(containerId) {
        try {
            const container = containerManager.getContainer(containerId);
            if (!container) return false;
            if (!containerManager.containerStates.get(containerId)) {
                containerManager.applyState(containerId, 'loading', true);
            }
            return true;
        } catch (error) {
            console.error('[AdFoxHelper] Error showing loader:', error);
            return false;
        }
    }

    return {
        get activeContainers() { return containerManager.activeContainers; },
        get containerStates() { return containerManager.containerStates; },

        get adFoxRegisteredContainers() { return adFoxRegisteredContainers; },
        getPlacementType,
        isSidebarPlacement,
        extractErrorCode,
        isRegistered: (containerId) => adFoxRegisteredContainers.has(containerId),
        checkAdFoxAvailability,
        destroyAdFox,
        create,
        clear,
        show,
        hide,
        showLoader
    };
}

let adFoxHelperInstance = null;

export default {
    getInstance() {
        if (!adFoxHelperInstance) {
            adFoxHelperInstance = createAdFoxHelper();
        }
        return adFoxHelperInstance;
    }
};
